import { useEffect, useRef, useState } from "react";
import { Search, Building2, Tag } from "lucide-react";
import api from "../lib/api";

/**
 * Dropdown of business/category suggestions shown on focus and while typing.
 * `inputRef` is the <input> to anchor to; `onPick` receives { type, label, value }.
 */
export default function SearchSuggestions({ query, open, onClose, onPick, categories = [] }) {
  const [businesses, setBusinesses] = useState([]);
  // Categories the search means, in any language ("imisatsi" → Hair salons).
  const [meant, setMeant] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    const id = ++requestRef.current;
    const q = query.trim();

    if (!q) {
      setBusinesses([]);
      setMeant([]);
      return;
    }

    setLoading(true);
    const timer = setTimeout(() => {
      api
        .get("/categories", { params: { q } })
        .then(({ data }) => {
          if (requestRef.current === id) setMeant((data || []).map((c) => c.name));
        })
        .catch(() => {
          if (requestRef.current === id) setMeant([]);
        });
      api
        .get("/businesses", { params: { q, per_page: 6 } })
        .then(({ data }) => {
          if (requestRef.current === id) setBusinesses(data.data || []);
        })
        .catch(() => {
          if (requestRef.current === id) setBusinesses([]);
        })
        .finally(() => {
          if (requestRef.current === id) setLoading(false);
        });
    }, 200);

    return () => clearTimeout(timer);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, onClose]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const matchedCategories = q
    ? [...new Set([...categories.filter((c) => c.toLowerCase().includes(q)), ...meant])].slice(0, 5)
    : categories.slice(0, 6);

  const hasResults = matchedCategories.length > 0 || businesses.length > 0;

  return (
    <div
      ref={boxRef}
      className="absolute left-0 right-0 top-full z-50 mt-2 max-h-96 overflow-y-auto rounded-2xl bg-white p-2 text-left text-slate-900 shadow-2xl"
    >
      {!q && (
        <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Popular categories
        </p>
      )}

      {matchedCategories.map((name) => (
        <button
          key={name}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick({ type: "category", value: name })}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm hover:bg-slate-100"
        >
          <Tag className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span>{name}</span>
        </button>
      ))}

      {q && (
        <>
          {matchedCategories.length > 0 && businesses.length > 0 && (
            <div className="my-1 h-px bg-slate-100" />
          )}
          {businesses.map((b) => (
            <button
              key={b.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick({ type: "business", value: b.name, slug: b.slug })}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm hover:bg-slate-100"
            >
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{b.name}</span>
            </button>
          ))}
        </>
      )}

      {q && loading && (
        <p className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400">
          <Search className="h-4 w-4 animate-pulse" /> Searching…
        </p>
      )}

      {q && !loading && !hasResults && (
        <p className="px-3 py-2 text-sm text-slate-400">No matches for "{query}"</p>
      )}
    </div>
  );
}
